import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { Modal } from "bootstrap";
import { reportGithubChangesByProject, updateTaskStatus } from "../../api";
import {
  firstGithubLinkHref,
  githubLinksFromRow,
  toGithubHref,
} from "../../utils/githubLinks";
import { completionStatusLabel } from "../../utils/completionStatus";
import { isDaniyalUserId } from "../../utils/isDaniyalUser";
import { getDateRangeOrderError } from "../../utils/dateRangeOrder";
import { toast } from "react-toastify";
import DeleteModal from "../DeleteModal";
import TablePagination from "./TablePagination";
import TruncatedTooltipCell from "./TruncatedTooltipCell";

const toIsoDateString = (value: unknown): string | null => {
  if (value == null || value === "") return null;
  const t = new Date(value as string | number | Date);
  return Number.isNaN(t.getTime()) ? null : t.toISOString().slice(0, 10);
};

/** Date range for `/report/github-changes` from task period / job dates (ISO yyyy-MM-dd). */
const githubReportDateRange = (task: any): { startDate: string; endDate: string } => {
  let start =
    toIsoDateString(task?.periodStartDate) ||
    toIsoDateString(task?.startDate);
  let end =
    toIsoDateString(task?.periodEndDate) ||
    toIsoDateString(task?.endDate) ||
    toIsoDateString(new Date());

  if (!end) end = new Date().toISOString().slice(0, 10);
  if (!start) {
    const endD = new Date(`${end}T12:00:00`);
    endD.setDate(endD.getDate() - 30);
    start = endD.toISOString().slice(0, 10);
  }
  if (start > end) {
    const tmp = start;
    start = end;
    end = tmp;
  }
  return { startDate: start, endDate: end };
};

type GithubAiSummaryModal = {
  repository: string | null;
  startDate: string | null;
  endDate: string | null;
  commitCount: number | null;
  aiSummary: string | null;
  aiSummaryError: string | null;
};

const parseGithubChangesForModal = (data: unknown): GithubAiSummaryModal => {
  if (data == null || typeof data !== "object") {
    return {
      repository: null,
      startDate: null,
      endDate: null,
      commitCount: null,
      aiSummary: null,
      aiSummaryError: "Unexpected response from the server.",
    };
  }
  const o = data as Record<string, unknown>;
  const commitCount =
    typeof o.commitCount === "number" && Number.isFinite(o.commitCount)
      ? o.commitCount
      : null;
  const aiSummaryError =
    o.aiSummaryError == null || o.aiSummaryError === ""
      ? null
      : String(o.aiSummaryError);
  const aiSummary =
    typeof o.aiSummary === "string" && o.aiSummary.trim() !== ""
      ? o.aiSummary
      : null;
  return {
    repository: typeof o.repository === "string" ? o.repository : null,
    startDate: typeof o.startDate === "string" ? o.startDate : null,
    endDate: typeof o.endDate === "string" ? o.endDate : null,
    commitCount,
    aiSummary,
    aiSummaryError,
  };
};

const showBootstrapModal = (elementId: string) => {
  const el = document.getElementById(elementId);
  if (el) Modal.getOrCreateInstance(el).show();
};

const displayOrDash = (v: unknown) => {
  if (v == null) return "—";
  const s = String(v).trim();
  return s === "" ? "—" : s;
};

/** `true` completed, `false` in progress, `null` unknown — drives status toggle visibility. */
const getJobCompletionState = (row: any): boolean | null => {
  if (typeof row?.statusBoolean === "boolean") return row.statusBoolean;
  if (row?.status === true) return true;
  if (row?.status === false) return false;
  const s = String(row?.status ?? "")
    .trim()
    .toLowerCase();
  if (s === "true" || s === "1" || s === "completed") return true;
  if (
    s === "false" ||
    s === "0" ||
    s === "in progress" ||
    s === "in_progress" ||
    s === "inprogress"
  ) {
    return false;
  }
  return null;
};

/** Iconify icon ids (Material Design Icons). */
const JOB_ICONS = {
  view: "mdi:eye-outline",
  edit: "mdi:pencil-outline",
  delete: "mdi:delete-outline",
  plus: "mdi:plus",
  github: "mdi:github",
  markComplete: "mdi:check-decagram-outline",
  markIncomplete: "mdi:toggle-switch-off-outline",
} as const;

const JobIcon = ({
  icon,
  size = 18,
}: {
  icon: string;
  size?: number;
}) => <Icon icon={icon} width={size} height={size} aria-hidden />;

const JobDetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div className="employee-job-detail-row d-flex flex-column flex-sm-row py-2 border-bottom border-light-subtle gap-1 gap-sm-3">
    <span className="employee-job-detail-label text-muted small text-uppercase fw-semibold flex-shrink-0">
      {label}
    </span>
    <div className="employee-job-detail-value text-break flex-grow-1 small">
      {children}
    </div>
  </div>
);

export type JobsProps = {
  user: { user_id?: string } | null | undefined;
  DANIYAL: string;
  tasks: any[] | undefined;
  searchQuery: string;
  handleSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  status: string;
  handleStatusChange: (e: any) => void;
  formatDate: (date: any) => string;
  setTaskId: (task: any) => void;
  handleEditClick: (task: any) => void;
  task: any;
  setTasks: React.Dispatch<React.SetStateAction<any>>;
  setTasks1: React.Dispatch<React.SetStateAction<any>>;
  setTasks2: React.Dispatch<React.SetStateAction<any>>;
  currentPage: number;
  totalPages: number;
  goToPage: (page: number) => void;
  goToPreviousPage: () => void;
  goToNextPage: () => void;
  refetchJobs: () => void | Promise<void>;
};

const Jobs = ({
  user,
  DANIYAL,
  tasks,
  searchQuery,
  handleSearchChange,
  status,
  handleStatusChange,
  formatDate,
  setTaskId,
  handleEditClick,
  task,
  setTasks,
  setTasks1,
  setTasks2,
  currentPage,
  totalPages,
  goToPage,
  goToPreviousPage,
  goToNextPage,
  refetchJobs,
}: JobsProps) => {
  const [viewTask, setViewTask] = useState<any | null>(null);
  const [githubReportLoading, setGithubReportLoading] = useState(false);
  const [githubAiModal, setGithubAiModal] =
    useState<GithubAiSummaryModal | null>(null);
  const [statusToggleBusyId, setStatusToggleBusyId] = useState<
    string | number | null
  >(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (githubAiModal) {
        setGithubAiModal(null);
        return;
      }
      if (viewTask) setViewTask(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [viewTask, githubAiModal]);

  /** After stacked overlays unmount, drop stray Bootstrap backdrops on `body` only. */
  useEffect(() => {
    if (viewTask || githubAiModal) return;
    const id = window.requestAnimationFrame(() => {
      document
        .querySelectorAll("body > .modal-backdrop")
        .forEach((el) => el.remove());
    });
    return () => window.cancelAnimationFrame(id);
  }, [viewTask, githubAiModal]);

  const fmtDate = (d: any) =>
    d == null || d === "" ? "—" : formatDate(d);

  const openLogOrSubtaskModal = (taskRow: any) => {
    setTaskId(taskRow);
    setViewTask(null);
    const modalId =
      isDaniyalUserId(user?.user_id, DANIYAL)
        ? "addSubtaskModal"
        : "exampleModalTogglee";
    window.setTimeout(() => showBootstrapModal(modalId), 120);
  };

  const openEditJobModal = (taskRow: any) => {
    setViewTask(null);
    handleEditClick(taskRow);
    window.setTimeout(() => showBootstrapModal("exampleModalToggle2"), 120);
  };

  const openDeleteJobModal = (taskRow: any) => {
    setTaskId(taskRow);
    setViewTask(null);
    window.setTimeout(() => showBootstrapModal("exampleModal4"), 120);
  };

  const patchTaskStatusInState = (taskId: string | number, completed: boolean) => {
    setTasks((prev: any) => {
      const list = Array.isArray(prev) ? prev : [];
      return list.map((t: any) =>
        t.taskId === taskId
          ? { ...t, status: completed, statusBoolean: completed }
          : t,
      );
    });
    setViewTask((prev: any) =>
      prev?.taskId === taskId
        ? { ...prev, status: completed, statusBoolean: completed }
        : prev,
    );
  };

  const handleToggleJobStatus = async (
    e: React.MouseEvent,
    taskRow: any,
  ) => {
    e.preventDefault();
    const id = taskRow?.taskId;
    const current = getJobCompletionState(taskRow);
    if (id == null || current === null || statusToggleBusyId != null) return;
    const nextComplete = !current;
    setStatusToggleBusyId(id);
    try {
      await updateTaskStatus({
        taskId: id,
        // userId: user?.user_id,
        // status: nextComplete,
      });
      patchTaskStatusInState(id, nextComplete);
    } catch {
      await Promise.resolve(refetchJobs());
    } finally {
      setStatusToggleBusyId(null);
    }
  };

  const handleGithubChangesReport = async (taskRow: any) => {
    const projectName = String(taskRow?.projectId ?? "").trim();
    if (!projectName || githubReportLoading) return;
    const { startDate, endDate } = githubReportDateRange(taskRow);
    const githubDerivedRangeErr = getDateRangeOrderError(startDate, endDate);
    if (githubDerivedRangeErr) {
      toast.error(githubDerivedRangeErr);
      return;
    }
    setGithubReportLoading(true);
    try {
      const res = await reportGithubChangesByProject({
        projectName,
        startDate,
        endDate,
      });
      setGithubAiModal(parseGithubChangesForModal(res.data));
    } catch {
      /* errors surfaced by API interceptor */
    } finally {
      setGithubReportLoading(false);
    }
  };

  const statusFilterValue = (() => {
    const raw = String(status ?? "")
      .trim()
      .toLowerCase();
    if (raw === "true") return "true";
    if (raw === "false") return "false";
    if (raw === "all") return "all";
    return "all";
  })();

  return (
    <>
      <div className="card-header d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center gap-2">
        <div className="heading">
          <h2>Jobs</h2>
        </div>
        <div className="button">
          <button
            type="button"
            className="btn btn-primary employee-jobs-add-btn"
            data-bs-target="#exampleModalToggle"
            data-bs-toggle="modal"
          >
            Add New Job
          </button>
        </div>
      </div>
      <div className="search-bar employee-jobs-toolbar">
        <div className="employee-jobs-search-field">
          <input
            type="text"
            placeholder="Search"
            className="form-control employee-jobs-toolbar-search-input"
            value={searchQuery}
            onChange={handleSearchChange}
            aria-label="Search jobs"
          />
        </div>
        {!isDaniyalUserId(user?.user_id, DANIYAL) && (
          <div className="employee-jobs-status-filter">
            <label className="employee-jobs-status-label" htmlFor="jobs-status-filter">
              Status
            </label>
            <select
              id="jobs-status-filter"
              value={statusFilterValue}
              onChange={handleStatusChange}
              className="status-dropdown employee-jobs-toolbar-status-dropdown"
              aria-label="Filter jobs by status"
            >
              <option value="all">All</option>
              <option value="true">Completed</option>
              <option value="false">In Progress</option>
            </select>
          </div>
        )}
      </div>

      <div className="table-responsive employee-jobs-table-responsive">
        <table className="table employee-jobs-table">
        <thead>
          <tr>
            <th scope="col" className="jobs-th-primary">
              Client group
            </th>
            <th scope="col" className="jobs-th-primary">
              Name
            </th>
            <th scope="col" className="jobs-th-num">
              Est hrs
            </th>
            <th scope="col" className="jobs-th-num">
              Est Mins
            </th>
            <th scope="col" className="jobs-th-date">
              End Date
            </th>
            <th scope="col" className="jobs-th-status text-center">
              Status
            </th>
            <th scope="col" className="jobs-th-github">
              GitHub links
            </th>
            <th scope="col" className="jobs-th-actions text-nowrap">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {tasks && tasks.length > 0 ? (
            tasks.map((taskRow: any, index: number) => {
              const completion = getJobCompletionState(taskRow);
              return (
              <tr key={String(taskRow.taskId ?? index)}>
                <td className="align-middle jobs-client-cell">
                  <TruncatedTooltipCell text={taskRow?.clientGroup} />
                </td>
                <td className="align-middle jobs-client-cell">
                  <TruncatedTooltipCell text={taskRow?.projectId} />
                </td>
                <td className="align-middle jobs-td-num">{taskRow.timeHrs}</td>
                <td className="align-middle jobs-td-num">{taskRow.timeMin}</td>
                <td className="align-middle jobs-td-date text-nowrap">
                  {formatDate(taskRow.endDate)}
                </td>
                <td className="align-middle text-center jobs-td-status">
                  {(() => {
                    const label = completionStatusLabel(
                      taskRow?.status,
                      taskRow?.statusBoolean,
                    );
                    if (label === "—") {
                      return <span className="text-muted">—</span>;
                    }
                    const done = label === "Completed";
                    return (
                      <span
                        className={`badge rounded-pill ${
                          done
                            ? "text-bg-success"
                            : "text-bg-warning text-dark"
                        }`}
                      >
                        {label}
                      </span>
                    );
                  })()}
                </td>
                <td className="text-center align-middle jobs-github-link-cell">
                  {(() => {
                    const links = githubLinksFromRow(taskRow);
                    if (links.length === 0) {
                      return <span className="text-muted">—</span>;
                    }
                    const first = links[0];
                    const extra = links.length - 1;
                    return (
                      <div
                        className="d-flex flex-column align-items-center gap-1 mx-auto jobs-github-table-compact"
                        style={{ maxWidth: "13rem" }}
                      >
                        <a
                          href={toGithubHref(first)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-truncate w-100 text-center jobs-github-table-link employee-job-detail-inline-link"
                          title={first}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {first}
                        </a>
                        {extra > 0 ? (
                          <span className="text-muted small">
                            +{extra} more in view
                          </span>
                        ) : null}
                      </div>
                    );
                  })()}
                </td>
                <td className="jobs-actions-cell align-middle">
                  <div
                    className="jobs-actions-toolbar"
                    role="group"
                    aria-label="Job actions"
                  >
                    <button
                      type="button"
                      className="jobs-actions-toolbar-btn"
                      title="View details"
                      aria-label="View job details"
                      onClick={() => setViewTask(taskRow)}
                    >
                      <JobIcon icon={JOB_ICONS.view} />
                    </button>
                    <button
                      type="button"
                      className="jobs-actions-toolbar-btn"
                      title="Edit job"
                      aria-label="Edit job"
                      onClick={() => openEditJobModal(taskRow)}
                    >
                      <JobIcon icon={JOB_ICONS.edit} />
                    </button>
                    {completion != null ? (
                      <button
                        type="button"
                        className="jobs-actions-toolbar-btn jobs-actions-toolbar-btn--status"
                        title={
                          completion
                            ? "Mark job as in progress"
                            : "Mark job as completed"
                        }
                        aria-label={
                          completion
                            ? "Mark job as in progress"
                            : "Mark job as completed"
                        }
                        disabled={statusToggleBusyId === taskRow.taskId}
                        onClick={(e) => void handleToggleJobStatus(e, taskRow)}
                      >
                        {statusToggleBusyId === taskRow.taskId ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : completion ? (
                          <JobIcon icon={JOB_ICONS.markIncomplete} size={20} />
                        ) : (
                          <JobIcon icon={JOB_ICONS.markComplete} size={20} />
                        )}
                      </button>
                    ) : null}
                    {taskRow.userId === user?.user_id && (
                      <button
                        type="button"
                        className="jobs-actions-toolbar-btn jobs-actions-toolbar-btn--delete"
                        title="Delete job"
                        aria-label="Delete job"
                        onClick={() => openDeleteJobModal(taskRow)}
                      >
                        <JobIcon icon={JOB_ICONS.delete} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
              );
            })
          ) : (
            <tr>
              <td colSpan={8} className="text-center">
                No Job found.
              </td>
            </tr>
          )}
        </tbody>
      </table>
      </div>

      {viewTask && (
          <div
            className="modal fade show d-block employee-job-detail-root"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-job-view-title"
          >
            <div className="modal-dialog modal-dialog-centered modal-xl modal-dialog-scrollable">
              <div className="modal-content border-0 shadow-lg employee-task-modal employee-job-detail-card">
                <div className="employee-job-detail-hero text-white p-4 pb-3 rounded-top position-relative">
                  <div className="d-flex flex-wrap justify-content-between align-items-start gap-3 pe-4">
                    <div>
                      <p className="text-white-50 small text-uppercase fw-semibold mb-1">
                        Client group
                      </p>
                      <h2
                        className="h4 mb-1 fw-semibold"
                        id="employee-job-view-title"
                      >
                        {displayOrDash(viewTask.clientGroup)}
                      </h2>
                      <p className="mb-0 opacity-90 fs-5">
                        {displayOrDash(viewTask.projectId)}
                      </p>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-2">
                      {(() => {
                        const label = completionStatusLabel(
                          viewTask?.status,
                          viewTask?.statusBoolean,
                        );
                        if (label === "—") return null;
                        const done = label === "Completed";
                        return (
                          <span
                            className={`badge rounded-pill px-3 py-2 fs-6 ${
                              done
                                ? "text-bg-success"
                                : "text-bg-warning text-dark"
                            }`}
                          >
                            {label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white position-absolute top-0 end-0 m-3"
                    aria-label="Close"
                    onClick={() => setViewTask(null)}
                  />
                </div>

                <div className="modal-body p-0">
                  <div className="px-4 pt-3 pb-2 employee-job-detail-metrics border-bottom">
                    <div className="row g-2">
                      <div className="col-6 col-md-3">
                        <div className="employee-job-detail-stat rounded-3 border bg-white p-3 h-100 shadow-sm">
                          <div className="text-muted small text-uppercase fw-semibold">
                            Est hrs
                          </div>
                          <div className="fs-4 fw-semibold employee-job-detail-stat-accent">
                            {displayOrDash(viewTask.timeHrs)}
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="employee-job-detail-stat rounded-3 border bg-white p-3 h-100 shadow-sm">
                          <div className="text-muted small text-uppercase fw-semibold">
                            Est mins
                          </div>
                          <div className="fs-4 fw-semibold employee-job-detail-stat-accent">
                            {displayOrDash(viewTask.timeMin)}
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="employee-job-detail-stat rounded-3 border bg-white p-3 h-100 shadow-sm">
                          <div className="text-muted small text-uppercase fw-semibold">
                            End date
                          </div>
                          <div className="fs-6 fw-semibold employee-job-detail-stat-accent">
                            {fmtDate(viewTask.endDate)}
                          </div>
                        </div>
                      </div>
                      <div className="col-6 col-md-3">
                        <div className="employee-job-detail-stat rounded-3 border bg-white p-3 h-100 shadow-sm">
                          <div className="text-muted small text-uppercase fw-semibold">
                            Owner
                          </div>
                          <div className="fs-6 fw-semibold text-truncate employee-job-detail-stat-accent">
                            {displayOrDash(viewTask.userId)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <div className="row g-4">
                      <div className="col-lg-6">
                        <h3 className="h6 employee-job-detail-section-title text-uppercase fw-semibold mb-3">
                          Schedule &amp; period
                        </h3>
                        <div className="rounded-3 border bg-light overflow-hidden employee-job-detail-info-panel">
                          <JobDetailRow label="Period type">
                            {displayOrDash(viewTask.periodType)}
                          </JobDetailRow>
                          <JobDetailRow label="Period start">
                            {fmtDate(viewTask.periodStartDate)}
                          </JobDetailRow>
                          <JobDetailRow label="Period end">
                            {fmtDate(viewTask.periodEndDate)}
                          </JobDetailRow>
                          <JobDetailRow label="Financial year">
                            {displayOrDash(viewTask.financialYear)}
                          </JobDetailRow>
                          <JobDetailRow label="Financial period">
                            {displayOrDash(viewTask.financialPeriod)}
                          </JobDetailRow>
                          <JobDetailRow label="Activity type">
                            {displayOrDash(viewTask.activityType)}
                          </JobDetailRow>
                        </div>
                      </div>
                      <div className="col-lg-6">
                        <h3 className="h6 employee-job-detail-section-title text-uppercase fw-semibold mb-3">
                          People &amp; links
                        </h3>
                        <div className="rounded-3 border bg-light overflow-hidden employee-job-detail-info-panel">
                          <JobDetailRow label="Team members">
                            {displayOrDash(viewTask.teamMembers)}
                          </JobDetailRow>
                          <JobDetailRow label="GitHub links">
                            {githubLinksFromRow(viewTask).length ? (
                              <div>
                                <ul className="list-unstyled mb-2 employee-job-github-link-list">
                                  {githubLinksFromRow(viewTask).map(
                                    (label, i) => (
                                      <li key={`${label}-${i}`} className="mb-1">
                                        <a
                                          href={toGithubHref(label)}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="fw-medium employee-job-detail-inline-link text-break"
                                        >
                                          {label}
                                        </a>
                                      </li>
                                    ),
                                  )}
                                </ul>
                                <label
                                  className="form-label small text-muted mb-1"
                                  htmlFor="employee-job-view-github-copy"
                                >
                                  All links (select or copy)
                                </label>
                                <textarea
                                  id="employee-job-view-github-copy"
                                  readOnly
                                  className="form-control form-control-sm font-monospace"
                                  rows={Math.min(
                                    10,
                                    githubLinksFromRow(viewTask).length + 2,
                                  )}
                                  value={githubLinksFromRow(viewTask).join(
                                    "\n",
                                  )}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary mt-2"
                                  onClick={() => {
                                    void navigator.clipboard?.writeText(
                                      githubLinksFromRow(viewTask).join("\n"),
                                    );
                                  }}
                                >
                                  Copy all
                                </button>
                              </div>
                            ) : (
                              "—"
                            )}
                          </JobDetailRow>
                        </div>
                      </div>
                    </div>

                    <div className="mt-4">
                      <h3 className="h6 employee-job-detail-section-title text-uppercase fw-semibold mb-2">
                        Description
                      </h3>
                      <div className="employee-job-detail-prose rounded-3 border bg-white p-3 small">
                        {displayOrDash(viewTask.projectDesc)}
                      </div>
                    </div>
                    <div className="mt-3">
                      <h3 className="h6 employee-job-detail-section-title text-uppercase fw-semibold mb-2">
                        Notes
                      </h3>
                      <div className="employee-job-detail-prose rounded-3 border bg-white p-3 small">
                        {displayOrDash(viewTask.notes)}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="modal-footer flex-wrap gap-2 justify-content-between align-items-stretch border-top employee-job-detail-modal-footer">
                  <div className="d-flex flex-wrap gap-2 align-items-center">
                    <button
                      type="button"
                      className="btn btn-primary employee-job-detail-action-btn d-inline-flex align-items-center gap-2"
                      onClick={() => openLogOrSubtaskModal(viewTask)}
                    >
                      <JobIcon icon={JOB_ICONS.plus} size={20} />
                      <span>
                        {isDaniyalUserId(user?.user_id, DANIYAL)
                          ? "Add sub-task"
                          : "Log hours"}
                      </span>
                    </button>
                    {getJobCompletionState(viewTask) != null ? (
                      <button
                        type="button"
                        className="btn btn-outline-light employee-job-detail-action-btn d-inline-flex align-items-center gap-2"
                        title={
                          getJobCompletionState(viewTask)
                            ? "Mark job as in progress"
                            : "Mark job as completed"
                        }
                        aria-label={
                          getJobCompletionState(viewTask)
                            ? "Mark job as in progress"
                            : "Mark job as completed"
                        }
                        disabled={statusToggleBusyId === viewTask.taskId}
                        onClick={(e) => void handleToggleJobStatus(e, viewTask)}
                      >
                        {statusToggleBusyId === viewTask.taskId ? (
                          <span
                            className="spinner-border spinner-border-sm"
                            role="status"
                            aria-hidden="true"
                          />
                        ) : getJobCompletionState(viewTask) ? (
                          <>
                            <JobIcon icon={JOB_ICONS.markIncomplete} size={20} />
                            <span>Mark in progress</span>
                          </>
                        ) : (
                          <>
                            <JobIcon icon={JOB_ICONS.markComplete} size={20} />
                            <span>Mark completed</span>
                          </>
                        )}
                      </button>
                    ) : null}
                  </div>
                  <div className="d-flex flex-wrap gap-2 justify-content-end">
                    <button
                      type="button"
                      className="btn btn-outline-secondary employee-job-detail-action-btn d-inline-flex align-items-center gap-2"
                      onClick={() => openEditJobModal(viewTask)}
                    >
                      <JobIcon icon={JOB_ICONS.edit} size={20} />
                      <span>Edit</span>
                    </button>
                    {viewTask.userId === user?.user_id && (
                      <button
                        type="button"
                        className="btn btn-outline-danger employee-job-detail-action-btn d-inline-flex align-items-center gap-2"
                        onClick={() => openDeleteJobModal(viewTask)}
                      >
                        <JobIcon icon={JOB_ICONS.delete} size={20} />
                        <span>Delete</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
      )}

      {githubAiModal && (
        <>
          <div
            className="employee-panel-overlay-backdrop fade show employee-github-ai-summary-backdrop"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) e.preventDefault();
            }}
            onClick={() => setGithubAiModal(null)}
          />
          <div
            className="modal fade show d-block employee-github-ai-summary-modal-root"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-github-ai-summary-title"
          >
            <div className="modal-dialog modal-dialog-centered modal-lg modal-dialog-scrollable">
              <div className="modal-content border-0 shadow-lg employee-task-modal employee-github-ai-summary-card">
                <div className="modal-header text-white border-0 py-3 px-4 employee-github-ai-summary-header">
                  <div className="me-2">
                    <h2
                      className="modal-title h5 mb-0 fw-semibold"
                      id="employee-github-ai-summary-title"
                    >
                      GitHub activity summary
                    </h2>
                    <p className="mb-0 mt-1 small text-white-50">
                      {githubAiModal.repository ?? "Repository"}
                      {githubAiModal.startDate && githubAiModal.endDate
                        ? ` · ${githubAiModal.startDate}${
                            githubAiModal.startDate === githubAiModal.endDate
                              ? ""
                              : ` → ${githubAiModal.endDate}`
                          }`
                        : null}
                      {githubAiModal.commitCount != null
                        ? ` · ${githubAiModal.commitCount} commit${
                            githubAiModal.commitCount === 1 ? "" : "s"
                          }`
                        : null}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-close btn-close-white flex-shrink-0"
                    aria-label="Close summary"
                    onClick={() => setGithubAiModal(null)}
                  />
                </div>
                <div className="modal-body px-4 py-3">
                  {githubAiModal.aiSummaryError ? (
                    <div
                      className="alert alert-danger mb-0"
                      role="alert"
                    >
                      {githubAiModal.aiSummaryError}
                    </div>
                  ) : null}
                  {githubAiModal.aiSummary ? (
                    <div
                      className={`employee-github-ai-summary-body rounded-3 border bg-white p-3 small ${
                        githubAiModal.aiSummaryError ? "mt-3" : ""
                      }`}
                    >
                      {githubAiModal.aiSummary}
                    </div>
                  ) : !githubAiModal.aiSummaryError ? (
                    <p className="text-muted mb-0">
                      No AI summary was returned for this range.
                    </p>
                  ) : null}
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      <DeleteModal
        task={task}
        setTasks={setTasks}
        setTasks1={setTasks1}
        setTasks2={setTasks2}
      />

      <TablePagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={goToPage}
        onPrevious={goToPreviousPage}
        onNext={goToNextPage}
        className="employee-table-pagination"
        ariaLabel="Jobs table pagination"
      />
    </>
  );
};

export default Jobs;
